// ===========================================
// COMPONENTE: ClientesFiltros
// ===========================================
// Barra de filtros compacta para la tabla de clientes

import * as React from 'react'
import { Search, X, SlidersHorizontal } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { MultiSelectFilter, FilterBadge } from '@/components/ui/multi-select-filter'

// Condiciones fiscales con nombres completos
const CONDICIONES_FISCALES = [
  { value: 'responsable_inscripto', label: 'Resp. Inscripto' },
  { value: 'monotributo', label: 'Monotributista' },
  { value: 'exento', label: 'Exento' },
  { value: 'consumidor_final', label: 'Cons. Final' },
  { value: 'no_responsable', label: 'No Responsable' },
  { value: 'monotributo_social', label: 'Mono Social' },
]

// Tipos de persona
const TIPOS_PERSONA = [
  { value: 'fisica', label: 'Física' },
  { value: 'sucesion_indivisa', label: 'Suc. Indivisa' },
  { value: 'juridica', label: 'Sociedad' },
]

export interface FiltrosState {
  busqueda: string
  tipoPersona: string[]
  estado: string
  responsable: string[]
  condicionFiscal: string[]
  impuestos: string[]
  provincia: string[]
  etiqueta: string[]
}

export interface ImpuestoDisponible {
  id: string
  codigo: string
  nombre?: string
}

export interface EtiquetaDisponible {
  id: string
  nombre: string
}

interface ClientesFiltrosProps {
  filtros: FiltrosState
  onChange: (filtros: FiltrosState) => void
  provinciasDisponibles: string[]
  impuestosDisponibles: ImpuestoDisponible[]
  responsablesDisponibles: string[]
  etiquetasDisponibles: EtiquetaDisponible[]
  className?: string
}

// Helper to get label from options (extraido fuera para estabilidad referencial)
function getLabel(options: { value: string; label: string }[], value: string) {
  return options.find((o) => o.value === value)?.label ?? value
}

export function ClientesFiltros({
  filtros,
  onChange,
  provinciasDisponibles,
  impuestosDisponibles,
  responsablesDisponibles,
  etiquetasDisponibles,
  className,
}: ClientesFiltrosProps) {
  const [moreFiltersOpen, setMoreFiltersOpen] = React.useState(false)

  // Options for responsable multi-select
  const responsableOptions = React.useMemo(
    () => responsablesDisponibles.map((r) => ({ value: r, label: r })),
    [responsablesDisponibles]
  )

  // Options for provincia multi-select
  const provinciaOptions = React.useMemo(
    () => provinciasDisponibles.map((p) => ({ value: p, label: p })),
    [provinciasDisponibles]
  )

  // Options for impuestos multi-select
  const impuestoOptions = React.useMemo(
    () => impuestosDisponibles.map((i) => ({ value: i.id, label: i.nombre || i.codigo })),
    [impuestosDisponibles]
  )

  // Options for etiqueta multi-select
  const etiquetaOptions = React.useMemo(
    () => etiquetasDisponibles.map((e) => ({ value: e.id, label: e.nombre })),
    [etiquetasDisponibles]
  )

  // Contar filtros activos (excluyendo busqueda)
  const filtrosActivos = React.useMemo(() => {
    let count = 0
    count += filtros.tipoPersona.length
    if (filtros.estado !== 'todos') count++
    count += filtros.responsable.length
    count += filtros.condicionFiscal.length
    count += filtros.provincia.length
    count += filtros.impuestos.length
    count += filtros.etiqueta.length
    return count
  }, [filtros])

  // Filtros secundarios activos (los que estan en "Mas filtros")
  const filtrosSecundariosActivos = React.useMemo(() => {
    let count = 0
    if (filtros.condicionFiscal.length > 0) count++
    if (filtros.provincia.length > 0) count++
    if (filtros.impuestos.length > 0) count++
    return count
  }, [filtros])

  // Handler para cambiar un campo
  const handleChange = React.useCallback((campo: keyof FiltrosState, valor: string | string[]) => {
    onChange({ ...filtros, [campo]: valor })
  }, [filtros, onChange])

  // Handler para limpiar todos los filtros
  const handleLimpiarFiltros = () => {
    onChange({
      busqueda: '',
      tipoPersona: [],
      estado: 'todos',
      responsable: [],
      condicionFiscal: [],
      impuestos: [],
      provincia: [],
      etiqueta: [],
    })
  }

  // Obtener nombre de impuesto por ID
  const getNombreImpuesto = React.useCallback((id: string) => {
    const impuesto = impuestosDisponibles.find((i) => i.id === id)
    return impuesto?.codigo || impuesto?.nombre || id
  }, [impuestosDisponibles])

  // Obtener nombre de etiqueta por ID
  const getNombreEtiqueta = React.useCallback((id: string) => {
    return etiquetasDisponibles.find((e) => e.id === id)?.nombre ?? id
  }, [etiquetasDisponibles])

  // All active filter badges
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- el useMemo es correcto: deps [filtros, impuestosDisponibles, etiquetasDisponibles] cubren todas las fuentes reactivas; el lint flagea el uso de handleChange/getNombreImpuesto/getNombreEtiqueta pero son estables vía useCallback con deps adecuadas
  const activeBadges = React.useMemo(() => {
    const badges: { key: string; prefix: string; label: string; onRemove: () => void }[] = []

    filtros.tipoPersona.forEach((v) => {
      badges.push({
        key: `tipo-${v}`,
        prefix: 'Tipo',
        label: getLabel(TIPOS_PERSONA, v),
        onRemove: () => handleChange('tipoPersona', filtros.tipoPersona.filter((x) => x !== v)),
      })
    })

    filtros.responsable.forEach((v) => {
      badges.push({
        key: `resp-${v}`,
        prefix: 'Resp',
        label: v,
        onRemove: () => handleChange('responsable', filtros.responsable.filter((x) => x !== v)),
      })
    })

    filtros.condicionFiscal.forEach((v) => {
      badges.push({
        key: `cond-${v}`,
        prefix: 'Cond',
        label: getLabel(CONDICIONES_FISCALES, v),
        onRemove: () => handleChange('condicionFiscal', filtros.condicionFiscal.filter((x) => x !== v)),
      })
    })

    filtros.provincia.forEach((v) => {
      badges.push({
        key: `prov-${v}`,
        prefix: 'Prov',
        label: v,
        onRemove: () => handleChange('provincia', filtros.provincia.filter((x) => x !== v)),
      })
    })

    filtros.impuestos.forEach((v) => {
      badges.push({
        key: `imp-${v}`,
        prefix: 'Imp',
        label: getNombreImpuesto(v),
        onRemove: () => handleChange('impuestos', filtros.impuestos.filter((x) => x !== v)),
      })
    })

    filtros.etiqueta.forEach((v) => {
      badges.push({
        key: `etq-${v}`,
        prefix: 'Etq',
        label: getNombreEtiqueta(v),
        onRemove: () => handleChange('etiqueta', filtros.etiqueta.filter((x) => x !== v)),
      })
    })

    return badges
  }, [filtros, impuestosDisponibles, etiquetasDisponibles])

  return (
    <div className={cn('space-y-2', className)}>
      {/* Fila unica: Busqueda + Filtros principales + Mas filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Busqueda */}
        <div className="relative min-w-[180px] flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, CUIT..."
            value={filtros.busqueda}
            onChange={(e) => handleChange('busqueda', e.target.value)}
            className="pl-8 h-9"
          />
        </div>

        {/* Tipo de Cliente - multi-select trigger */}
        <MultiSelectFilter
          label="Tipo"
          options={TIPOS_PERSONA}
          value={filtros.tipoPersona}
          onChange={(v) => handleChange('tipoPersona', v)}
          triggerClassName="w-[165px]"
        />

        {/* Responsable - multi-select trigger con busqueda */}
        <MultiSelectFilter
          label="Resp"
          options={responsableOptions}
          value={filtros.responsable}
          onChange={(v) => handleChange('responsable', v)}
          searchable
          triggerClassName="w-[200px]"
        />

        {/* Etiqueta - multi-select trigger con busqueda (solo si la organizacion tiene etiquetas) */}
        {etiquetasDisponibles.length > 0 && (
          <MultiSelectFilter
            label="Etiqueta"
            options={etiquetaOptions}
            value={filtros.etiqueta}
            onChange={(v) => handleChange('etiqueta', v)}
            searchable
            triggerClassName="w-[200px]"
          />
        )}

        {/* Mas filtros (Estado, Cond. Fiscal, Provincia, Impuestos) */}
        <Popover open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'h-9 gap-1.5',
                filtrosSecundariosActivos > 0 && 'border-primary text-primary'
              )}
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span className="hidden sm:inline">Más filtros</span>
              {filtrosSecundariosActivos > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {filtrosSecundariosActivos}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3 max-h-[70vh] overflow-y-auto" align="start">
            <div className="space-y-3">
              <p className="text-sm font-medium">Filtros adicionales</p>

              {/* Condicion Fiscal - multi-select embedded */}
              <MultiSelectFilter
                variant="embedded"
                label="Condición Fiscal"
                options={CONDICIONES_FISCALES}
                value={filtros.condicionFiscal}
                onChange={(v) => handleChange('condicionFiscal', v)}
                maxHeight="max-h-40"
              />

              {/* Provincia - multi-select embedded con busqueda */}
              <MultiSelectFilter
                variant="embedded"
                label="Provincia"
                options={provinciaOptions}
                value={filtros.provincia}
                onChange={(v) => handleChange('provincia', v)}
                searchable
                maxHeight="max-h-32"
              />

              {/* Impuestos - multi-select embedded */}
              <MultiSelectFilter
                variant="embedded"
                label="Impuestos"
                options={impuestoOptions}
                value={filtros.impuestos}
                onChange={(v) => handleChange('impuestos', v)}
                maxHeight="max-h-32"
              />
              <p className="text-xs text-muted-foreground">
                Muestra clientes con todos los seleccionados
              </p>

              {/* Limpiar filtros secundarios */}
              {filtrosSecundariosActivos > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    onChange({
                      ...filtros,
                      condicionFiscal: [],
                      provincia: [],
                      impuestos: [],
                    })
                  }}
                >
                  Limpiar estos filtros
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Limpiar todo (solo si hay filtros activos) */}
        {filtrosActivos > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLimpiarFiltros}
            className="h-9 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Limpiar</span>
          </Button>
        )}
      </div>

      {/* Badges de filtros activos - espacio reservado para evitar layout shift */}
      <div className="min-h-[28px] flex items-center gap-1.5 flex-wrap">
        {activeBadges.map((badge) => (
          <FilterBadge
            key={badge.key}
            prefix={badge.prefix}
            label={badge.label}
            onRemove={badge.onRemove}
          />
        ))}
      </div>
    </div>
  )
}

